import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Users, ExternalLink, MapPinned, Vote, MessagesSquare, Landmark, ShieldCheck, Search,
  SlidersHorizontal, Globe, Share2, Newspaper, UserX, Map as MapIcon, XCircle, HelpCircle, FileText,
  LocateFixed, Loader2, MousePointerClick, Mail, Phone, CalendarClock, Flag, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, Loading, EmptyState, formatDate } from '../components/Shared';
import { WardOverlays, WardSourceAttribution, FitBounds, wardBounds } from '../components/WardMap';
import { findWardForPoint } from '../lib/geo';

const CKL_LIST_URL = 'https://www.kawarthalakes.ca/government-administration/elections/candidates-list/';
const WARD_BOUNDARIES_URL = 'https://www.kawarthalakes.ca/government-administration/elections/ward-boundaries/';
const INTERACTIVE_MAP_URL = 'http://kawartha.maps.arcgis.com/apps/webappviewer/index.html?id=755ecf940c624e96801b3335f4a131d2';
const PDF_MAP_URL = 'https://www.kawarthalakes.ca/media/dypbezhr/wardsfinal_30x48_nov20182.pdf';

// Friendly Eastern-time formatting for the public refresh timestamps
const fmtEasternPublic = (iso) => {
  if (!iso) return 'Not recorded yet';
  try {
    return new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Toronto', dateStyle: 'medium', timeStyle: 'short' }) + ' ET';
  } catch { return iso; }
};

export const STATUS_BADGES = {
  unofficial: { label: 'Unofficial', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  certified: { label: 'Certified', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  withdrawn: { label: 'Withdrawn', cls: 'bg-red-50 text-red-700 border-red-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 border-red-200' },
  needs_review: { label: 'Needs Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

const REVIEW_BADGES = {
  source_based: { label: 'Source-Based', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
  needs_review: { label: 'Needs Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  verified: { label: 'Verified', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  sample: { label: 'Sample', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

const UPDATE_SOURCE_BADGES = {
  official: { label: 'Official Source', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  local_news: { label: 'Local News', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  steves_comment: { label: "Steve's Comment", cls: 'bg-[#1a2744] text-white border-0' },
};

const FILTERS = [
  { key: 'all', label: 'All offices' },
  { key: 'mayor', label: 'Mayor' },
  { key: 'ward-1', label: 'Ward 1' },
  { key: 'ward-2', label: 'Ward 2' },
  { key: 'ward-3', label: 'Ward 3' },
  { key: 'ward-4', label: 'Ward 4' },
  { key: 'ward-5', label: 'Ward 5' },
  { key: 'ward-6', label: 'Ward 6' },
  { key: 'ward-7', label: 'Ward 7' },
  { key: 'ward-8', label: 'Ward 8' },
  { key: 'trustee', label: 'School Board Trustee' },
  { key: 'third-party', label: 'Third Party Advertisers' },
  { key: 'withdrawn', label: 'Withdrawn Candidates' },
];

const matchesFilter = (c, filter) => {
  switch (filter) {
    case 'all': return true;
    case 'mayor': return c.office === 'Mayor';
    case 'trustee': return c.candidate_type === 'school_board_trustee' || c.office.includes('Trustee');
    case 'third-party': return c.candidate_type === 'third_party_advertiser' || c.office.includes('Third Party');
    case 'withdrawn': return c.status === 'withdrawn';
    default: {
      const m = filter.match(/^ward-(\d)$/);
      return m ? c.ward === parseInt(m[1], 10) && c.office.startsWith('Councillor') : true;
    }
  }
};

export const CandidateCard = ({ c, compact = false }) => {
  const status = STATUS_BADGES[c.status] || STATUS_BADGES.unofficial;
  const review = REVIEW_BADGES[c.review_status];
  return (
    <Card className="card-lift h-full" data-testid={`candidate-card-${c.id}`}>
      <CardContent className={compact ? 'p-4' : 'p-5'}>
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <Badge variant="outline" className={`${status.cls} font-semibold text-xs`} data-testid="candidate-status-badge">{status.label}</Badge>
          {review && c.status !== 'withdrawn' && <Badge variant="outline" className={`${review.cls} font-semibold text-xs`} data-testid="candidate-review-badge">{review.label}</Badge>}
        </div>
        <p className="font-serif font-bold text-[#1a2744] leading-snug" data-testid="candidate-name">{c.display_name}</p>
        <p className="text-sm text-slate-600 mt-0.5" data-testid="candidate-office">
          {c.office}{c.ward && !c.office.includes('Ward') ? ` · Ward ${c.ward}` : ''}
        </p>
        {c.status === 'withdrawn' && (c.withdrawn_date || c.filing_date) && (
          <p className="text-xs text-red-700 mt-1">
            {c.filing_date && <>Filed {formatDate(c.filing_date)}</>}{c.filing_date && c.withdrawn_date && ' · '}{c.withdrawn_date && <>Withdrawn {formatDate(c.withdrawn_date)}</>}
          </p>
        )}
        {c.public_notes && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.public_notes}</p>}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
          {c.contact_email && (
            <a href={`mailto:${c.contact_email}`} className="inline-flex items-center gap-1 min-h-11 sm:min-h-0 py-2 sm:py-0 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4 break-all" data-testid="candidate-email-link">
              <Mail className="h-3.5 w-3.5 shrink-0" /> {c.contact_email}
            </a>
          )}
          {c.contact_phone && (
            <a href={`tel:${c.contact_phone.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-1 min-h-11 sm:min-h-0 py-2 sm:py-0 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="candidate-phone-link">
              <Phone className="h-3.5 w-3.5 shrink-0" /> {c.contact_phone}
            </a>
          )}
          {c.website_url && (
            <a href={c.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="candidate-website-link">
              <Globe className="h-3.5 w-3.5" /> Website
            </a>
          )}
          {c.social_url && (
            <a href={c.social_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="candidate-social-link">
              <Share2 className="h-3.5 w-3.5" /> Social
            </a>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed" data-testid="candidate-source-line">
          Source: <a href={c.source_url || CKL_LIST_URL} target="_blank" rel="noreferrer" className="underline">{c.source_name || 'City of Kawartha Lakes Candidates List'}</a>
          {c.source_checked_date && <> · Last checked {formatDate(c.source_checked_date)}</>}
        </p>
      </CardContent>
    </Card>
  );
};

export const Ward8RaceBlock = ({ candidates, meta }) => {
  const steve = candidates.find((c) => c.is_campaign);
  const others = candidates.filter((c) => !c.is_campaign && c.ward === 8 && c.office.startsWith('Councillor'));
  const lastChecked = others[0]?.source_checked_date || steve?.source_checked_date || '';
  return (
    <section className="bg-[#1a2744] rounded-2xl overflow-hidden" data-testid="ward8-race-block">
      <div className="p-5 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A84C] mb-1.5">WARD 8 SPOTLIGHT</p>
        <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">Ward 8 Councillor Race</h2>
        <p className="text-slate-300 text-sm sm:text-base mt-2 leading-relaxed max-w-3xl" data-testid="ward8-race-intro">
          Ward 8 residents will elect one Councillor to represent them at City Council. This section tracks the official candidate list and updates as the election moves forward.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          {steve && (
            <div className="bg-white/10 border border-[#C9A84C]/60 rounded-xl p-5" data-testid="ward8-steve-card">
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                <Badge className="bg-[#C9A84C] text-[#1a2744] border-0 font-bold text-xs">Campaign Card</Badge>
                <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 font-semibold text-xs">{STATUS_BADGES[steve.status]?.label || 'Unofficial'}</Badge>
              </div>
              <p className="font-serif font-black text-white text-lg">Steve Lesperance</p>
              <p className="text-sm text-slate-300">Ward 8 Councillor Candidate</p>
              <p className="text-sm font-bold text-[#C9A84C] mt-1" data-testid="ward8-steve-slogan">Lesperance is the French word for Hope</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button asChild size="sm" className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="ward8-meet-steve-btn"><Link to="/meet-steve">Meet Steve</Link></Button>
                <Button asChild size="sm" variant="outline" className="h-11 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press" data-testid="ward8-platform-btn"><Link to="/platform">Steve's Platform</Link></Button>
                <Button asChild size="sm" variant="outline" className="h-11 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press" data-testid="ward8-action-hub-btn"><Link to="/issue-tracker">Ward 8 Issue Tracker App (PITA)</Link></Button>
              </div>
            </div>
          )}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="ward8-official-candidates">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-3">Official Ward 8 candidates (CKL list)</p>
            {others.length === 0 ? (
              <p className="text-sm text-slate-400">No other Ward 8 candidates are listed on the official CKL candidates list yet.</p>
            ) : (
              <div className="space-y-3">
                {others.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-2.5 last:border-0 last:pb-0" data-testid={`ward8-candidate-row-${c.id}`}>
                    <div>
                      <p className="text-white font-bold text-sm">{c.display_name}</p>
                      <p className="text-slate-400 text-xs">{c.office}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className={`${(STATUS_BADGES[c.status] || STATUS_BADGES.unofficial).cls} font-semibold text-xs`}>{(STATUS_BADGES[c.status] || STATUS_BADGES.unofficial).label}</Badge>
                      {REVIEW_BADGES[c.review_status] && <Badge variant="outline" className={`${REVIEW_BADGES[c.review_status].cls} font-semibold text-xs`}>{REVIEW_BADGES[c.review_status].label}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3">
              Source: <a href={CKL_LIST_URL} target="_blank" rel="noreferrer" className="underline">{meta?.official_source_name || 'City of Kawartha Lakes Candidates List'}</a>
              {lastChecked && <> · Last checked {formatDate(lastChecked)}</>}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const UpdateCard = ({ u }) => {
  const src = UPDATE_SOURCE_BADGES[u.source_type] || UPDATE_SOURCE_BADGES.official;
  return (
    <Card data-testid="candidate-update-item">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
          <Badge variant="outline" className={`${src.cls} font-semibold text-xs`}>{src.label}</Badge>
          {u.ward && <Badge variant="outline" className="border-slate-300 text-slate-600 text-xs">Ward {u.ward}</Badge>}
          {u.office && <Badge variant="outline" className="border-slate-300 text-slate-600 text-xs">{u.office}</Badge>}
        </div>
        <p className="font-serif font-bold text-[#1a2744] leading-snug">{u.title}</p>
        <p className="text-xs text-slate-500 mt-1">
          {formatDate(u.date)}
          {u.candidate_name && <> · {u.candidate_name}</>}
          {u.source_name && <> · Source: {u.source_url ? <a href={u.source_url} target="_blank" rel="noreferrer" className="underline">{u.source_name}</a> : u.source_name}</>}
        </p>
        {u.summary && <p className="text-sm text-slate-700 mt-2 leading-relaxed">{u.summary}</p>}
      </CardContent>
    </Card>
  );
};

const FilterList = ({ active, onSelect, counts, idPrefix = 'sidebar' }) => (
  <div className="flex flex-col gap-1" data-testid={`whos-running-filter-list-${idPrefix}`}>
    {FILTERS.map((f) => (
      <button
        key={f.key}
        onClick={() => onSelect(f.key)}
        className={`flex items-center justify-between text-left px-3.5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 min-h-11 ${active === f.key ? 'bg-[#1a2744] text-white' : 'text-[#1a2744] hover:bg-slate-100'}`}
        data-testid={`whos-running-filter-${idPrefix}-${f.key}`}
      >
        <span className="flex items-center gap-2">{f.key === 'withdrawn' && <UserX className="h-3.5 w-3.5" />}{f.label}</span>
        <span className={`text-xs font-bold ${active === f.key ? 'text-[#C9A84C]' : 'text-slate-400'}`}>{counts[f.key] ?? 0}</span>
      </button>
    ))}
  </div>
);

// ---------- Ward popup content (candidate-focused) ----------
const WardPopupContent = ({ w, count, onViewCandidates, onViewDetails }) => (
  <div className="text-sm" data-testid={`ward-popup-${w.ward_number}`}>
    <p className="font-bold text-base leading-snug">{w.label || `Ward ${w.ward_number}`}{w.ward_number === 8 ? ' (Steve\'s campaign ward)' : ''}</p>
    {w.description && <p className="text-xs text-slate-600 mt-1 leading-relaxed" style={{ maxHeight: 72, overflow: 'hidden' }}>{w.description}</p>}
    <p className="text-xs text-slate-500 mt-1.5">One Councillor is elected for this ward.</p>
    <p className="text-xs mt-1"><strong>{count}</strong> candidate{count === 1 ? '' : 's'} listed for Ward {w.ward_number}</p>
    {w.ward_number === 8 && (
      <p className="text-xs mt-1.5 font-semibold text-[#1a2744]">Steve Lesperance is running for Ward 8 Councillor.</p>
    )}
    <div className="mt-2 flex flex-col gap-1.5">
      <button onClick={() => onViewCandidates(w.ward_number)} className="text-xs font-bold underline text-left" data-testid={`ward-popup-view-candidates-${w.ward_number}`}>View Candidates in This Ward</button>
      <button onClick={() => onViewDetails(w)} className="text-xs font-bold underline text-left" data-testid={`ward-popup-view-details-${w.ward_number}`}>View Ward Boundary Details</button>
      {w.ward_number === 8 && (
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          <Link to="/meet-steve" className="text-xs font-bold underline">Meet Steve</Link>
          <Link to="/platform" className="text-xs font-bold underline">Steve's Platform</Link>
          <Link to="/issue-tracker" className="text-xs font-bold underline">Ward 8 Issue Tracker App (PITA)</Link>
        </span>
      )}
      <a href={w.source_url || WARD_BOUNDARIES_URL} target="_blank" rel="noreferrer" className="text-[10px] text-slate-500 underline">Source: {w.source_name || 'City of Kawartha Lakes Ward Boundaries'}</a>
    </div>
  </div>
);

// ---------- Ward details drawer ----------
const WardDetailsDrawer = ({ ward, wards, candidates, open, onOpenChange, onViewCandidates }) => {
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if (open) { const t = setTimeout(() => setMapReady(true), 350); return () => { clearTimeout(t); setMapReady(false); }; }
  }, [open, ward?.ward_number]);
  if (!ward) return null;
  const n = ward.ward_number;
  const wardCandidates = candidates.filter((c) => !c.is_campaign && c.ward === n && c.office.startsWith('Councillor'));
  const steve = n === 8 ? candidates.find((c) => c.is_campaign) : null;
  const bounds = wardBounds(wards, n);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="ward-details-drawer">
        <SheetHeader>
          <SheetTitle className="text-[#1a2744] font-serif text-left">{ward.label || `Ward ${n}`}{n === 8 ? ' (Steve\'s campaign ward)' : ''}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="h-52 rounded-xl overflow-hidden border border-slate-200" data-testid="ward-details-map">
            {mapReady && (
              <MapContainer center={[44.3, -78.6]} zoom={9} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} dragging={false} zoomControl={false}>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {bounds && <FitBounds bounds={bounds} />}
                <WardOverlays wards={wards} focusWard={n} showLabels interactive={false} />
              </MapContainer>
            )}
          </div>
          {ward.description && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Official boundary description</p>
              <p className="text-sm text-slate-700 leading-relaxed" data-testid="ward-details-description">{ward.description}</p>
            </div>
          )}
          <p className="text-sm text-slate-600">One Councillor is elected in this ward. The Mayor is elected City-wide.</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
            <a href={ward.source_url || WARD_BOUNDARIES_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="ward-details-source-link">
              <ExternalLink className="h-3.5 w-3.5" /> Official Ward Boundaries
            </a>
            {ward.boundary_pdf_url && (
              <a href={ward.boundary_pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="ward-details-pdf-link">
                <FileText className="h-3.5 w-3.5" /> Ward {n} Boundary Map (PDF)
              </a>
            )}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Candidates running in Ward {n}</p>
            {steve && (
              <div className="bg-[#1a2744] rounded-xl p-4 mb-3" data-testid="ward-details-steve-card">
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                  <Badge className="bg-[#C9A84C] text-[#1a2744] border-0 font-bold text-xs">Campaign Card</Badge>
                  <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 font-semibold text-xs">{STATUS_BADGES[steve.status]?.label || 'Unofficial'}</Badge>
                </div>
                <p className="font-serif font-bold text-white">Steve Lesperance</p>
                <p className="text-xs text-slate-300">Ward 8 Councillor Candidate · Lesperance is the French word for Hope</p>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <Button asChild size="sm" className="h-9 text-xs bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press"><Link to="/meet-steve">Meet Steve</Link></Button>
                  <Button asChild size="sm" variant="outline" className="h-9 text-xs border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press"><Link to="/platform">Platform</Link></Button>
                </div>
              </div>
            )}
            {wardCandidates.length === 0 ? (
              <p className="text-sm text-slate-500">No candidates are listed for Ward {n} on the official CKL candidates list yet.</p>
            ) : (
              <div className="space-y-2.5">{wardCandidates.map((c) => <CandidateCard key={c.id} c={c} compact />)}</div>
            )}
          </div>
          <Button onClick={() => { onOpenChange(false); onViewCandidates(n); }} className="h-12 w-full bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="ward-details-view-candidates-btn">
            <Users className="h-4 w-4 mr-1.5" /> View candidate cards for Ward {n}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ---------- Address -> Ward finder (privacy-first) ----------
// Addresses are only sent to the backend geocode proxy (POST), never stored,
// never logged, never sent to AI, and never shown publicly.
const MATCH_LABELS = {
  exact: { text: 'Exact match', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  approximate: { text: 'Approximate', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  postal_code: { text: 'Postal code area', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  intersection: { text: 'Intersection', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
};

const AddressWardFinder = ({ config, onSelect, inputRef }) => {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [hint, setHint] = useState('');
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [notFoundFlagged, setNotFoundFlagged] = useState('');
  const timerRef = useRef(null);
  const boxRef = useRef(null);
  const seqRef = useRef(0);          // stale-response guard: only the newest search may update the list
  const lastQueryRef = useRef('');

  const debounceMs = Math.max(Number(config?.debounce_ms) || 400, 650);
  const minChars = Number(config?.min_chars) || 4;

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const runSearch = useCallback(async (q) => {
    const query = (q || '').trim();
    if (query.length < minChars) return;
    if (query === lastQueryRef.current && searched) { setOpen(true); return; }
    const seq = ++seqRef.current;
    setSearching(true);
    setError('');
    setOpen(true);
    try {
      const r = await api.post('/ward-finder/geocode', { q: query });
      if (seq !== seqRef.current) return;   // a newer search is in flight; ignore this result
      lastQueryRef.current = query;
      setSuggestions(r.data?.suggestions || []);
      setHint(r.data?.hint || '');
      setSearched(true);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setSuggestions([]);
      setSearched(true);
      setOpen(false);
      setError(e.response?.data?.detail || 'Address lookup is temporarily unavailable. You can still use the map below.');
    } finally {
      if (seq === seqRef.current) setSearching(false);
    }
  }, [minChars, searched]);

  const search = useCallback((q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSearched(false);
    if (!q || q.trim().length < minChars) { setSuggestions([]); setHint(''); setOpen(false); setSearching(false); seqRef.current += 1; return; }
    timerRef.current = setTimeout(() => runSearch(q), debounceMs);
  }, [debounceMs, minChars, runSearch]);

  const searchNow = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    runSearch(value);
  }, [runSearch, value]);

  const isAdmin = !!localStorage.getItem('w8_token');

  if (!config?.provider_ready) {
    return (
      <div data-testid="ward-finder-fallback">
        <p className="font-serif font-bold text-[#1a2744]">{config?.title || 'Find Your Ward by Address'}</p>
        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed" data-testid="ward-finder-fallback-message">
          {config?.fallback_message || 'Address lookup is being prepared. You can still use the map below to find your ward.'}
        </p>
        {isAdmin && (
          <p className="text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2" data-testid="ward-finder-admin-warning">
            Admin note: no address autocomplete/geocoding provider is configured yet. Configure it in Admin → 2026 Election Centre → Wards &amp; Map → Ward Finder.
          </p>
        )}
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative" data-testid="ward-finder-search">
      <label className="font-serif font-bold text-[#1a2744] block" htmlFor="ward-finder-input">{config.title}</label>
      <p className="text-xs text-slate-600 mt-1 mb-2 leading-relaxed" data-testid="ward-finder-helper">{config.helper_text}</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            id="ward-finder-input"
            ref={inputRef}
            value={value}
            onChange={(e) => { setValue(e.target.value); search(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchNow(); } if (e.key === 'Escape') setOpen(false); }}
            onFocus={() => { if (suggestions.length || searched) setOpen(true); }}
            placeholder={config.placeholder}
            autoComplete="off"
            className="h-12 pl-9 pr-9 bg-white"
            role="combobox"
            aria-expanded={open}
            aria-controls="ward-finder-suggestions"
            data-testid="ward-finder-input"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" data-testid="ward-finder-searching" />}
          {!searching && value && (
            <button aria-label="Clear address" onClick={() => { setValue(''); setSuggestions([]); setHint(''); setSearched(false); setOpen(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="ward-finder-input-clear">
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button type="button" onClick={searchNow} disabled={searching || value.trim().length < minChars} className="h-12 px-4 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press shrink-0" data-testid="ward-finder-search-button">
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5" data-testid="ward-finder-format-hint">Works with street addresses (1041 Highway 7, Omemee), rural addresses (1234 County Road 121), intersections (Highway 7 and Meadowview Road) and postal codes. Pick the match that is really yours.</p>
      {open && (
        <ul id="ward-finder-suggestions" role="listbox" className="sm:absolute sm:z-[500] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden sm:max-h-80 sm:overflow-y-auto" data-testid="ward-finder-suggestions">
          {searching && (
            <li className="px-3.5 py-3 text-sm text-slate-500 flex items-center gap-2" data-testid="ward-finder-searching-row"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching official address data...</li>
          )}
          {!searching && searched && suggestions.length === 0 && (
            <li className="px-3.5 py-3 text-sm text-slate-600" data-testid="ward-finder-no-results-hint">
              {hint || config.no_results_hint || 'Can\'t find your address? Try typing "123 Main St, Kawartha Lakes, Ontario". Adding "Kawartha Lakes", "Ontario" or "Canada", or using "St" instead of "Street" often helps.'}
              <div className="mt-2">
                {notFoundFlagged === lastQueryRef.current ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800" data-testid="ward-finder-not-found-flagged"><CheckCircle2 className="h-3.5 w-3.5" /> Flagged for Steve to review. House numbers are not stored.</span>
                ) : (
                  <button type="button" className="inline-flex items-center gap-1 text-xs font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c] min-h-9" data-testid="ward-finder-not-found-flag"
                    onClick={async () => {
                      const q = lastQueryRef.current;
                      try { await api.post('/ward-finder/feedback', { reason: 'not_found', query: q, chosen_label: '', match_type: '', ward_shown: null, note: '' }); setNotFoundFlagged(q); } catch (e) { /* silent: nothing else to do for the visitor */ }
                    }}>
                    <Flag className="h-3.5 w-3.5" /> Flag this: my address should have been found
                  </button>
                )}
              </div>
            </li>
          )}
          {!searching && suggestions.length > 0 && (
            <li className="px-3.5 pt-2.5 pb-1 text-[10px] font-black uppercase tracking-wider text-slate-500" data-testid="ward-finder-confirm-prompt">Confirm your location</li>
          )}
          {!searching && suggestions.map((s, i) => {
            const m = MATCH_LABELS[s.match_type] || MATCH_LABELS.approximate;
            return (
              <li key={`${s.lat}-${s.lng}-${i}`} role="option" aria-selected="false">
                <button
                  className="w-full text-left px-3.5 py-3 text-sm text-[#1a2744] hover:bg-slate-100 focus-visible:bg-slate-100 border-b border-slate-100 last:border-0 min-h-11"
                  onClick={() => { setOpen(false); setValue(s.label); onSelect({ ...s, typed_query: lastQueryRef.current }); }}
                  data-testid={`ward-finder-suggestion-${i}`}
                >
                  <span className="flex items-start gap-1.5">
                    <MapPinned className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#C9A84C]" />
                    <span className="flex-1">
                      <span className="block leading-snug">{s.label}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 ${m.cls}`} data-testid={`ward-finder-suggestion-match-${i}`}>{m.text}</span>
                        {s.ward ? (
                          <span className="inline-block text-[10px] font-bold uppercase tracking-wide bg-[#eaf2ff] text-[#1a2744] border border-[#c9d8f0] rounded px-1.5 py-0.5" data-testid={`ward-finder-suggestion-ward-${i}`}>Ward {s.ward}</span>
                        ) : (
                          <span className="inline-block text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5" data-testid={`ward-finder-suggestion-ward-${i}`}>{s.ward_status === 'boundary_uncertain' ? 'On a ward boundary' : 'Outside ward polygons'}</span>
                        )}
                      </span>
                      {s.note && <span className="block text-[11px] text-slate-500 mt-1 leading-snug" data-testid={`ward-finder-suggestion-note-${i}`}>{s.note}</span>}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {error && <p className="text-xs text-red-700 mt-1.5" data-testid="ward-finder-error">{error}</p>}
      <p className="text-[11px] text-slate-500 mt-2 leading-relaxed" data-testid="ward-finder-privacy-note">{config.privacy_note}</p>
    </div>
  );
};

/* "This isn't my location": lets a visitor flag a miss for Steve to review.
   Privacy: the server stores the query and match with house/unit numbers and
   postal codes redacted. No coordinates, no IP, no contact details. */
const FEEDBACK_REASONS = [
  ['wrong_place', 'Matched the wrong place'],
  ['wrong_community', 'Right street, wrong community'],
  ['not_found', 'My address was not found'],
  ['wrong_ward', 'The ward shown looks wrong'],
  ['other', 'Something else'],
];

const LocationFeedback = ({ result }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('wrong_place');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { setOpen(false); setDone(''); setError(''); setNote(''); setReason('wrong_place'); }, [result?.label]);

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const r = await api.post('/ward-finder/feedback', {
        reason,
        query: result?.typed_query || '',
        chosen_label: result?.label || '',
        match_type: result?.match_type || '',
        ward_shown: result?.ward?.ward_number ?? null,
        note,
      });
      setDone(r.data?.message || 'Thanks, this lookup has been flagged for review.');
      setOpen(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not send right now. Please try again shortly.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return <p className="mt-3 text-xs font-semibold text-emerald-800 inline-flex items-start gap-1.5" data-testid="ward-finder-feedback-done"><CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {done}</p>;
  }
  return (
    <div className="mt-3" data-testid="ward-finder-feedback">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1a2744] underline decoration-slate-300 underline-offset-4 hover:decoration-[#b91c1c] min-h-11" data-testid="ward-finder-feedback-button">
          <Flag className="h-3.5 w-3.5" /> This isn't my location
        </button>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ward-finder-feedback-form">
          <p className="font-serif font-bold text-[#1a2744] text-sm">What went wrong?</p>
          <RadioGroup value={reason} onValueChange={setReason} className="mt-2 grid gap-2">
            {FEEDBACK_REASONS.map(([val, label]) => (
              <div key={val} className="flex items-center gap-2">
                <RadioGroupItem value={val} id={`wf-reason-${val}`} data-testid={`ward-finder-feedback-reason-${val}`} />
                <Label htmlFor={`wf-reason-${val}`} className="text-sm font-normal text-slate-700 cursor-pointer">{label}</Label>
              </div>
            ))}
          </RadioGroup>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={240} placeholder="Optional: what should it have found? (no need to include your house number)" className="mt-3 h-11 text-sm" data-testid="ward-finder-feedback-note" />
          <p className="text-[11px] text-slate-500 mt-2" data-testid="ward-finder-feedback-privacy">Only the street, community and match are kept; house numbers and postal codes are removed before saving. No contact details are collected.</p>
          {error && <p className="text-xs text-red-700 mt-2" data-testid="ward-finder-feedback-error">{error}</p>}
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={send} disabled={sending} className="h-10 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="ward-finder-feedback-submit">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Flag className="h-3.5 w-3.5 mr-1" />} Send to Steve
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="h-10 text-xs font-bold btn-press" data-testid="ward-finder-feedback-cancel">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const WardFinderResultPanel = ({ result, config, counts, onViewCandidates, onViewDetails, onClear }) => {
  if (!result) return null;
  const ward = result.ward;
  if (!ward) {
    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="ward-finder-no-match">
        <CardContent className="p-4">
          <p className="font-serif font-bold text-amber-900">{result.ward_status === 'boundary_uncertain' ? 'This location sits on a ward boundary' : 'No confident ward match'}</p>
          <p className="text-sm text-amber-900 mt-1.5 leading-relaxed">
            {result.ward_status === 'boundary_uncertain'
              ? 'The point is right on the line between wards, so we will not guess. Please confirm your ward with the official City of Kawartha Lakes ward boundary map.'
              : config?.no_match_message}
          </p>
          <p className="text-xs text-amber-800 mt-2 break-words">Address searched: {result.label}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button asChild size="sm" variant="outline" className="h-10 text-xs border-amber-300 text-amber-900 font-bold btn-press"><a href={config?.source_url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Official Ward Boundary Map</a></Button>
            <Button size="sm" variant="outline" onClick={onClear} className="h-10 text-xs border-amber-300 text-amber-900 font-bold btn-press" data-testid="ward-finder-clear-btn"><XCircle className="h-3.5 w-3.5 mr-1" /> Clear Address Search</Button>
          </div>
          <LocationFeedback result={result} />
        </CardContent>
      </Card>
    );
  }
  const n = ward.ward_number;
  const count = counts[`ward-${n}`] ?? 0;
  const mt = result.match_type || 'exact';
  const approx = mt !== 'exact';
  const quality = mt === 'postal_code'
    ? 'Postal code area only. A postal code is not proof of your ward: it covers an area that can cross ward lines. Enter your street address to confirm.'
    : mt === 'intersection'
      ? 'Intersection located. The ward shown is the official ward at that exact point.'
      : approx
        ? 'Approximate location (street level). Confirm with your full address or the official City of Kawartha Lakes ward boundary map.'
        : 'Exact address match found. Ward determined from the official ward boundary at this address.';
  return (
    <Card className="border-[#C9A84C]/70 bg-[#FBF7EE]" data-testid="ward-finder-result">
      <CardContent className="p-4 sm:p-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#8a6d2c]">WARD MATCH</p>
        {approx ? (
          <p className="font-serif font-bold text-lg text-[#1a2744] mt-1" data-testid="ward-finder-result-title">This location appears to be in Ward {n}, but please verify with the official City of Kawartha Lakes ward boundary map.</p>
        ) : (
          <p className="font-serif font-bold text-lg text-[#1a2744] mt-1" data-testid="ward-finder-result-title">Your address appears to be in Ward {n}</p>
        )}
        <p className={`text-xs font-bold mt-1.5 ${approx ? 'text-amber-800' : 'text-emerald-800'}`} data-testid="ward-finder-match-quality">{quality}</p>
        {result.note && <p className="text-xs text-slate-600 mt-1" data-testid="ward-finder-result-note">{result.note}</p>}
        <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">One Councillor is elected for this ward. Below are the candidates listed for this ward.</p>
        <p className="text-xs text-slate-600 mt-2 break-words" data-testid="ward-finder-result-address">Address searched: {result.label}</p>
        <p className="text-xs font-bold text-[#1a2744] mt-1.5" data-testid="ward-finder-result-count">{count} candidate{count === 1 ? '' : 's'} listed for Ward {n}</p>
        {n === 8 && (
          <p className="text-sm font-bold text-[#1a2744] mt-2" data-testid="ward-finder-result-steve">Steve Lesperance is running for Ward 8 Councillor.</p>
        )}
        <div className="flex flex-wrap gap-2 mt-3.5">
          <Button size="sm" onClick={() => onViewCandidates(n)} className="h-11 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="ward-finder-view-candidates-btn"><Users className="h-3.5 w-3.5 mr-1" /> View Candidates in Ward {n}</Button>
          <Button size="sm" variant="outline" onClick={() => onViewDetails(ward)} className="h-11 text-xs border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="ward-finder-view-details-btn"><MapPinned className="h-3.5 w-3.5 mr-1" /> View Ward Boundary Details</Button>
          <Button size="sm" variant="outline" onClick={onClear} className="h-11 text-xs border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="ward-finder-clear-btn"><XCircle className="h-3.5 w-3.5 mr-1" /> Clear Address Search</Button>
          {n === 8 && (
            <>
              <Button asChild size="sm" className="h-11 text-xs bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="ward-finder-meet-steve-btn"><Link to="/meet-steve">Meet Steve</Link></Button>
              <Button asChild size="sm" variant="outline" className="h-11 text-xs border-slate-300 text-[#1a2744] font-bold btn-press"><Link to="/platform">Steve's Platform</Link></Button>
              <Button asChild size="sm" variant="outline" className="h-11 text-xs border-slate-300 text-[#1a2744] font-bold btn-press"><Link to="/issue-tracker">Ward 8 Issue Tracker App (PITA)</Link></Button>
            </>
          )}
        </div>
        <LocationFeedback result={result} />
        <p className="text-[11px] text-slate-500 mt-3">
          Ward match is based on the official ward boundary polygons. Verify at{' '}
          <a href={ward.source_url || config?.source_url} target="_blank" rel="noreferrer" className="underline" data-testid="ward-finder-result-source">{ward.source_name || config?.source_name || 'City of Kawartha Lakes Ward Boundaries'}</a>.
        </p>
      </CardContent>
    </Card>
  );
};

const TOP_BUTTONS = [
  { label: 'Official CKL Candidates List', href: CKL_LIST_URL, external: true, icon: ExternalLink },
  { label: 'Election Centre', to: '/elections', icon: Landmark },
  { label: 'Ward Boundaries', to: '/elections/ward-boundaries', icon: MapPinned },
  { label: 'How to Vote', to: '/elections/how-to-vote', icon: Vote },
  { label: 'Debate Watch', to: '/elections/debates', icon: MessagesSquare },
];

export default function WhosRunning() {
  const [candidates, setCandidates] = useState([]);
  const [meta, setMeta] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [wards, setWards] = useState([]);
  const [wrSettings, setWrSettings] = useState({});
  const [wfConfig, setWfConfig] = useState(null);
  const [addressResult, setAddressResult] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [focusWard, setFocusWard] = useState(null);
  const [detailsWard, setDetailsWard] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const mapSectionRef = useRef(null);
  const ward8Ref = useRef(null);
  const directoryRef = useRef(null);
  const addressInputRef = useRef(null);

  useEffect(() => {
    Promise.allSettled([
      api.get('/candidates'),
      api.get('/candidates/meta'),
      api.get('/candidates/updates'),
      api.get('/wards'),
      api.get('/settings'),
      api.get('/ward-finder/config'),
    ]).then(([c, m, u, w, s, wf]) => {
      if (c.status === 'fulfilled') setCandidates(c.value.data?.candidates || []);
      if (m.status === 'fulfilled') setMeta(m.value.data);
      if (u.status === 'fulfilled') setUpdates(Array.isArray(u.value.data) ? u.value.data : []);
      if (w.status === 'fulfilled') setWards(w.value.data?.wards || []);
      if (s.status === 'fulfilled') setWrSettings(s.value.data?.whos_running_map || {});
      if (wf.status === 'fulfilled') setWfConfig(wf.value.data);
      setLoading(false);
    });
  }, []);

  // #find-your-ward deep link (homepage / election centre buttons)
  useEffect(() => {
    if (!loading && window.location.hash === '#find-your-ward') {
      setTimeout(() => mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
    }
  }, [loading]);

  const showFindYourWard = wrSettings.show_find_your_ward !== false;
  const showWardCards = wrSettings.show_ward_cards !== false;
  const featureWard8 = wrSettings.feature_ward8 !== false;

  const directory = useMemo(() => candidates.filter((c) => !c.is_campaign), [candidates]);

  const counts = useMemo(() => {
    const out = {};
    FILTERS.forEach((f) => { out[f.key] = directory.filter((c) => matchesFilter(c, f.key)).length; });
    return out;
  }, [directory]);

  const visible = useMemo(() => {
    let list = directory.filter((c) => matchesFilter(c, filter));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((c) => `${c.display_name} ${c.office} ward ${c.ward || ''} ${c.source_name} ${c.status.replace('_', ' ')}`.toLowerCase().includes(q));
    }
    return list;
  }, [directory, filter, query]);

  const grouped = useMemo(() => {
    const map = new Map();
    visible.forEach((c) => {
      const key = c.status === 'withdrawn' && filter === 'withdrawn' ? 'Withdrawn Candidates' : c.office;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return Array.from(map.entries());
  }, [visible, filter]);

  const activeWardNum = useMemo(() => {
    const m = filter.match(/^ward-(\d)$/);
    return m ? parseInt(m[1], 10) : null;
  }, [filter]);

  const sortedWards = useMemo(() => [...wards].sort((a, b) => (a.sort_order ?? a.ward_number) - (b.sort_order ?? b.ward_number)), [wards]);

  // ---------- ward interactions ----------
  const viewCandidatesInWard = useCallback((n) => {
    setFilter(`ward-${n}`);
    setQuery('');
    setFocusWard(n);
    setTimeout(() => {
      if (n === 8) ward8Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else directoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, []);

  const viewWardOnMap = useCallback((n) => {
    setFocusWard(n);
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const clearWardFilter = useCallback(() => { setFilter('all'); setFocusWard(null); }, []);

  const backToAllWards = useCallback(() => {
    setFilter('all');
    setFocusWard(null);
    setAddressResult(null);
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const openWardDetails = useCallback((w) => { setDetailsWard(w); setDrawerOpen(true); }, []);

  // ---------- address -> ward finder ----------
  const handleAddressSelect = useCallback((s) => {
    // Point-in-polygon against the official ward boundary GeoJSON.
    // Never guess: only match when the geocoded point clearly falls inside a polygon.
    // Server computed the ward from the official polygons for this exact point;
    // the client-side check is the fallback. Neither ever guesses a ward.
    let ward = findWardForPoint(s.lat, s.lng, wards) || null;
    if (!ward && s.ward) ward = wards.find((w) => Number(w.ward_number) === Number(s.ward)) || null;
    setAddressResult({ label: s.label, lat: s.lat, lng: s.lng, match_type: s.match_type || 'exact', ward, note: s.note || '', ward_status: s.ward_status || '', typed_query: s.typed_query || '' });
    if (ward) {
      setFocusWard(ward.ward_number);
      setFilter(`ward-${ward.ward_number}`);
    } else {
      setFocusWard(null);
    }
  }, [wards]);

  const clearAddressSearch = useCallback(() => {
    setAddressResult(null);
    setFocusWard(null);
    setFilter('all');
  }, []);

  const focusAddressInput = useCallback(() => {
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => addressInputRef.current?.focus(), 400);
  }, []);

  const useMapInstead = useCallback(() => {
    setAddressResult(null);
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Map bounds: matched ward > unmatched address marker > focused ward > all wards
  const mapBounds = useMemo(() => {
    if (addressResult && addressResult.ward) return wardBounds(wards, addressResult.ward.ward_number);
    if (addressResult && !addressResult.ward) {
      const { lat, lng } = addressResult;
      return [[lat - 0.03, lng - 0.045], [lat + 0.03, lng + 0.045]];
    }
    if (focusWard) return wardBounds(wards, focusWard);
    return wardBounds(wards, null);
  }, [addressResult, focusWard, wards]);

  if (loading) return <Loading />;

  const activeLabel = FILTERS.find((f) => f.key === filter)?.label || 'All offices';

  return (
    <div>
      <PageHeader
        pageKey="whos-running"
        kicker="NEUTRAL · SOURCE-BASED · OFFICIAL CKL LIST"
        title="Who's Running in the 2026 Kawartha Lakes Municipal Election"
        subtitle="Follow the official candidate list, ward races, mayoral race, school board trustees, withdrawals, and election updates."
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 overflow-x-hidden">
        {/* Top buttons */}
        <div className="flex flex-wrap gap-2.5" data-testid="whos-running-top-buttons">
          {TOP_BUTTONS.map((b) => b.external ? (
            <Button key={b.label} asChild className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="whos-running-official-list-btn">
              <a href={b.href} target="_blank" rel="noreferrer"><b.icon className="h-4 w-4 mr-1.5" /> {b.label}</a>
            </Button>
          ) : (
            <Button key={b.label} asChild variant="outline" className="h-11 border-slate-300 text-[#1a2744] font-bold btn-press" data-testid={`whos-running-btn-${b.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Link to={b.to}><b.icon className="h-4 w-4 mr-1.5" /> {b.label}</Link>
            </Button>
          ))}
        </div>

        {/* Official wording + disclaimer */}
        <Card className="mt-5 border-slate-200"><CardContent className="p-4 sm:p-5">
          <p className="font-serif font-bold text-[#1a2744]" data-testid="official-list-title">{meta?.official_list_title || 'Unofficial List of Candidates for the 2026 Municipal Election'}</p>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed" data-testid="whos-running-disclaimer">{meta?.public_disclaimer}</p>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid="whos-running-links-disclaimer">{meta?.links_disclaimer}</p>
        </CardContent></Card>

        {/* Official source + refresh status strip */}
        <Card className="mt-4 border-[#C9A84C]/50 bg-[#FBF7EE]" data-testid="whos-running-source-strip"><CardContent className="p-4 sm:p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="min-w-0">
              <p className="text-sm font-bold text-[#1a2744]" data-testid="whos-running-source-line">Source: City of Kawartha Lakes Candidates List</p>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed max-w-2xl">
                This page is updated from the City's official candidates list. Candidate information can change; residents should confirm details directly with the City of Kawartha Lakes.
              </p>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600" data-testid="whos-running-refresh-times">
                <span data-testid="whos-running-last-checked"><strong className="text-[#1a2744]">Last checked:</strong> {fmtEasternPublic(meta?.refresh?.last_checked)}</span>
                <span data-testid="whos-running-last-updated"><strong className="text-[#1a2744]">Last updated:</strong> {fmtEasternPublic(meta?.refresh?.last_updated)}</span>
                <span className="inline-flex items-center gap-1" data-testid="whos-running-next-refresh">
                  <CalendarClock className="h-3.5 w-3.5 text-[#8a6d2c]" />
                  <strong className="text-[#1a2744]">Next scheduled refresh:</strong> {meta?.refresh?.next_refresh ? `${fmtEasternPublic(meta.refresh.next_refresh)} (${meta?.refresh?.schedule_label || 'Daily at 7:00 PM Eastern'})` : (meta?.refresh?.schedule_label || 'Daily at 7:00 PM Eastern')}
                </span>
              </div>
            </div>
            <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press shrink-0" data-testid="whos-running-official-source-btn">
              <a href={CKL_LIST_URL} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> View the Official Source</a>
            </Button>
          </div>
        </CardContent></Card>

        {/* ================= FIND YOUR WARD ================= */}
        {showFindYourWard && (
          <section id="find-your-ward" ref={mapSectionRef} className="mt-10 scroll-mt-24" data-testid="find-your-ward-section">
            <SectionHeader
              kicker="WARD BOUNDARIES"
              title="Find Your Ward"
              dek="Not sure what ward you are in? Use the map below to understand the ward boundaries and see who is running in your area."
            />
            <p className="text-slate-700 leading-relaxed max-w-3xl -mt-2 mb-4" data-testid="find-your-ward-body">
              Many residents do not know which ward they live in. This map helps you see the 8 wards of the City of Kawartha Lakes, understand the ward boundaries, and quickly find the candidates running in your area.
            </p>
            <div className="flex flex-wrap gap-2.5 mb-4">
              <Button onClick={focusAddressInput} className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="find-ward-search-address-btn"><LocateFixed className="h-4 w-4 mr-1.5" /> Search Address</Button>
              <Button onClick={backToAllWards} variant="outline" className="h-11 border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="find-ward-view-all-btn"><MapIcon className="h-4 w-4 mr-1.5" /> View All Wards</Button>
              <Button onClick={() => viewCandidatesInWard(8)} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="find-ward-ward8-btn"><Vote className="h-4 w-4 mr-1.5" /> View Ward 8 Race</Button>
              {addressResult && (
                <>
                  <Button onClick={useMapInstead} variant="outline" className="h-11 border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="find-ward-use-map-btn"><MousePointerClick className="h-4 w-4 mr-1.5" /> Use Map Instead</Button>
                  <Button onClick={clearAddressSearch} variant="outline" className="h-11 border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="find-ward-clear-search-btn"><XCircle className="h-4 w-4 mr-1.5" /> Clear Search</Button>
                </>
              )}
              <Button asChild variant="outline" className="h-11 border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="find-ward-official-boundaries-btn"><a href={WARD_BOUNDARIES_URL} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> Official Ward Boundaries</a></Button>
              <Button asChild variant="outline" className="h-11 border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="find-ward-official-list-btn"><a href={CKL_LIST_URL} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> Official Candidates List</a></Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 font-semibold text-xs" data-testid="find-ward-source-badge">Source: City of Kawartha Lakes Ward Boundaries</Badge>
              {focusWard && <Badge className="bg-[#C9A84C] text-[#1a2744] border-0 font-bold text-xs" data-testid="find-ward-focus-badge">Focused: Ward {focusWard}</Badge>}
            </div>

            {/* Address search + result panel beside the map on desktop; stacked on mobile */}
            <div className="lg:grid lg:grid-cols-[400px_1fr] lg:gap-5 lg:items-start">
              <div className="space-y-4 mb-5 lg:mb-0">
                <Card className="border-slate-200"><CardContent className="p-4 sm:p-5">
                  <AddressWardFinder config={wfConfig} onSelect={handleAddressSelect} inputRef={addressInputRef} />
                </CardContent></Card>
                <WardFinderResultPanel
                  result={addressResult}
                  config={wfConfig}
                  counts={counts}
                  onViewCandidates={viewCandidatesInWard}
                  onViewDetails={openWardDetails}
                  onClear={clearAddressSearch}
                />
              </div>

              {/* Interactive all-wards map */}
              {wards.length > 0 ? (
                <div className="rounded-2xl overflow-hidden border border-slate-200" data-testid="whos-running-ward-map">
                  <div className="h-[320px] sm:h-[420px] lg:h-[480px]">
                    <MapContainer center={[44.3, -78.6]} zoom={9} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      {mapBounds && <FitBounds bounds={mapBounds} />}
                      <WardOverlays
                        wards={featureWard8 ? wards.map((w) => (w.ward_number === 8 ? { ...w, border_weight: Math.max(Number(w.border_weight) || 2, 3.5), overlay_opacity: Math.max(Number(w.overlay_opacity) || 0.18, 0.3) } : w)) : wards}
                        focusWard={addressResult?.ward ? addressResult.ward.ward_number : focusWard}
                        showLabels
                        interactive
                        popupRenderer={(w) => (
                          <WardPopupContent w={w} count={counts[`ward-${w.ward_number}`] ?? 0} onViewCandidates={viewCandidatesInWard} onViewDetails={openWardDetails} />
                        )}
                      />
                      {addressResult && (
                        <CircleMarker
                          center={[addressResult.lat, addressResult.lng]}
                          radius={9}
                          pathOptions={{ color: '#1a2744', weight: 2.5, fillColor: '#C9A84C', fillOpacity: 0.95 }}
                          data-testid="ward-finder-marker"
                        />
                      )}
                    </MapContainer>
                  </div>
                  <div className="bg-white px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
                    <WardSourceAttribution />
                    <p className="text-[11px] text-slate-500">Tap a ward to see who is running there.{featureWard8 ? ' Ward 8 is highlighted as Steve\'s campaign ward.' : ''}</p>
                  </div>
                </div>
              ) : (
                <EmptyState title="Ward map unavailable" subtitle="Ward boundary overlays are being updated. Use the official CKL ward boundary map linked above." />
              )}
            </div>

            {/* Source facts */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ward-facts-row">
              {[
                ['8 wards', 'The City of Kawartha Lakes has 8 wards.'],
                ['8 Councillors + Mayor', 'There are 8 Councillors plus the Mayor. The Mayor is elected City-wide.'],
                ['1 Councillor per ward', 'One Councillor is elected per ward.'],
              ].map(([t, d]) => (
                <Card key={t}><CardContent className="p-4">
                  <p className="font-black text-[#1a2744]">{t}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{d}</p>
                </CardContent></Card>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
              <a href={INTERACTIVE_MAP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="official-interactive-map-link">
                <MapPinned className="h-3.5 w-3.5" /> Official Interactive Kawartha Lakes Ward Boundary Map
              </a>
              <a href={PDF_MAP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="official-pdf-map-link">
                <FileText className="h-3.5 w-3.5" /> PDF Ward Boundary Map
              </a>
            </div>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed max-w-3xl" data-testid="find-ward-note">
              Ward boundaries and candidate information should always be verified through official City of Kawartha Lakes election resources.
            </p>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed max-w-3xl" data-testid="ward-finder-disclaimer">
              {wfConfig?.disclaimer || 'This tool is provided to help residents understand ward boundaries. Ward results should be verified with the official City of Kawartha Lakes ward boundary resources.'}{' '}
              Source: <a href={wfConfig?.source_url || WARD_BOUNDARIES_URL} target="_blank" rel="noreferrer" className="underline font-semibold">{wfConfig?.source_name || 'City of Kawartha Lakes Ward Boundaries'}</a>
            </p>
          </section>
        )}

        {/* Ward 8 race block */}
        <div className="mt-8 scroll-mt-24" ref={ward8Ref}>
          <Ward8RaceBlock candidates={candidates} meta={meta} />
        </div>

        {/* ================= BROWSE BY WARD ================= */}
        {showWardCards && sortedWards.length > 0 && (
          <section className="mt-10" data-testid="browse-by-ward-section">
            <SectionHeader kicker="WARD DIRECTORY" title="Browse by Ward" dek="Pick your ward to see its boundary summary and the candidates running there." />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {sortedWards.map((w) => {
                const n = w.ward_number;
                const count = counts[`ward-${n}`] ?? 0;
                const isW8 = n === 8 && featureWard8;
                return (
                  <Card key={n} className={`card-lift ${isW8 ? 'md:col-span-2 bg-[#1a2744] border-[#C9A84C]/60' : ''}`} data-testid={`ward-card-${n}`}>
                    <CardContent className="p-4 h-full flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-4 w-4 rounded-sm border" style={{ backgroundColor: w.overlay_color || '#1a2744', borderColor: w.border_color || '#1a2744', opacity: 0.85 }} aria-hidden="true" />
                        <p className={`font-black ${isW8 ? 'text-white' : 'text-[#1a2744]'}`}>{w.label || `Ward ${n}`}</p>
                        {isW8 && <Badge className="bg-[#C9A84C] text-[#1a2744] border-0 font-bold text-xs">Steve's race</Badge>}
                      </div>
                      <p className={`text-xs mt-2 leading-relaxed line-clamp-3 ${isW8 ? 'text-slate-300' : 'text-slate-600'}`}>
                        {isW8 ? 'Ward 8 is the race Steve Lesperance is running in.' : (w.card_summary || w.description || 'See the official ward boundary description.')}
                      </p>
                      <p className={`text-xs font-bold mt-2 ${isW8 ? 'text-[#C9A84C]' : 'text-[#1a2744]'}`} data-testid={`ward-card-count-${n}`}>
                        {count} candidate{count === 1 ? '' : 's'} listed
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3 pt-2 mt-auto">
                        {isW8 ? (
                          <>
                            <Button size="sm" onClick={() => viewCandidatesInWard(8)} className="h-10 text-xs bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="ward-card-8-view-candidates">View Ward 8 Candidates</Button>
                            <Button asChild size="sm" variant="outline" className="h-10 text-xs border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press"><Link to="/meet-steve">Meet Steve</Link></Button>
                            <Button asChild size="sm" variant="outline" className="h-10 text-xs border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press"><Link to="/platform">Steve's Platform</Link></Button>
                            <Button asChild size="sm" variant="outline" className="h-10 text-xs border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press"><Link to="/issue-tracker">Ward 8 Issue Tracker App (PITA)</Link></Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" onClick={() => viewCandidatesInWard(n)} className="h-10 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`ward-card-view-candidates-${n}`}>View Candidates</Button>
                            <Button size="sm" variant="outline" onClick={() => viewWardOnMap(n)} className="h-10 text-xs border-slate-300 text-[#1a2744] font-bold btn-press" data-testid={`ward-card-view-map-${n}`}>View Ward on Map</Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* ================= DIRECTORY ================= */}
        <div ref={directoryRef} className="scroll-mt-24">
          {/* Search + mobile filter trigger */}
          <div className="mt-10 flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search candidate name, office, ward, or source."
                className="h-12 pl-9 bg-white"
                data-testid="whos-running-search-input"
              />
            </div>
            <div className="lg:hidden">
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="h-12 border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="whos-running-filters-button">
                    <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[290px] overflow-y-auto bg-white">
                  <SheetHeader><SheetTitle className="text-[#1a2744] font-serif text-left">Filter candidates</SheetTitle></SheetHeader>
                  <div className="mt-4">
                    <FilterList idPrefix="drawer" active={filter} counts={counts} onSelect={(k) => { setFilter(k); setFiltersOpen(false); }} />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="mt-5 lg:grid lg:grid-cols-[240px_1fr] lg:gap-6">
            <aside className="hidden lg:block">
              <div className="bg-white rounded-2xl border border-slate-200 p-3" data-testid="whos-running-sidebar-filters">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 px-2 pb-2">Filter by office</p>
                <FilterList active={filter} counts={counts} onSelect={setFilter} />
              </div>
            </aside>

            <div>
              {/* Ward filter heading + controls */}
              {activeWardNum ? (
                <div className="mb-4" data-testid="ward-filter-heading-block">
                  <h3 className="text-lg font-black text-[#1a2744]" data-testid="ward-filter-heading">Candidates Running in Ward {activeWardNum}</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={clearWardFilter} className="h-10 text-xs border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="ward-clear-filter-btn"><XCircle className="h-3.5 w-3.5 mr-1" /> Clear Filter</Button>
                    <Button size="sm" variant="outline" onClick={clearWardFilter} className="h-10 text-xs border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="ward-back-all-candidates-btn"><Users className="h-3.5 w-3.5 mr-1" /> Back to All Candidates</Button>
                    <Button size="sm" variant="outline" onClick={backToAllWards} className="h-10 text-xs border-slate-300 text-[#1a2744] font-bold btn-press" data-testid="ward-back-all-wards-btn"><MapIcon className="h-3.5 w-3.5 mr-1" /> Back to All Wards</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 mb-4">
                  <p className="text-sm font-bold text-[#1a2744]" data-testid="whos-running-active-filter">
                    {activeLabel} <span className="text-slate-400 font-semibold">· {visible.length} {visible.length === 1 ? 'listing' : 'listings'}</span>
                  </p>
                  {meta?.last_source_check && <p className="text-[11px] text-slate-500 hidden sm:block">Last source check: {formatDate(meta.last_source_check.slice(0, 10))}</p>}
                </div>
              )}

              {visible.length === 0 ? (
                <EmptyState title="No candidates match this view" subtitle="Try a different office filter or clear the search. Third party advertisers appear once they are listed on the official CKL page." />
              ) : (
                <div className="space-y-8" data-testid="whos-running-directory">
                  {grouped.map(([office, list]) => (
                    <div key={office} data-testid={`office-group-${office.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="h-4 w-4 text-[#C9A84C]" />
                        <h3 className="font-serif font-bold text-[#1a2744]">{office}</h3>
                        <span className="text-xs font-bold text-slate-400">({list.length})</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {list.map((c) => <CandidateCard key={c.id} c={c} />)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="mt-6 text-xs text-slate-500 leading-relaxed" data-testid="whos-running-contact-note">
                Looking for full candidate contact details? Candidates choose what contact information appears publicly. See the{' '}
                <a href={CKL_LIST_URL} target="_blank" rel="noreferrer" className="underline font-semibold">official CKL Candidates List</a>.
              </p>
            </div>
          </div>
        </div>

        {/* ================= HOW DO I KNOW WHAT WARD I AM IN? ================= */}
        <section className="mt-12" data-testid="ward-help-section">
          <Card className="border-slate-200"><CardContent className="p-5 sm:p-7">
            <div className="flex items-start gap-3">
              <HelpCircle className="h-6 w-6 text-[#C9A84C] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-serif text-xl font-bold text-[#1a2744]">How Do I Know What Ward I Am In?</h3>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed max-w-3xl" data-testid="ward-help-text">
                  Ward boundaries decide which Councillor race you vote in. If you are unsure what ward you live in, use the ward map above and compare it with the official City of Kawartha Lakes Ward Boundaries page.
                </p>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed max-w-3xl" data-testid="ward-help-note">
                  For official confirmation, residents should verify their ward using the City of Kawartha Lakes ward boundary resources and election information. This site cannot determine your ward from an address. Please verify your ward using the official City of Kawartha Lakes ward boundary map.
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm">
                  {[
                    ['Official Ward Boundaries', WARD_BOUNDARIES_URL, true],
                    ['Election Centre', '/elections', false],
                    ['How to Vote', '/elections/how-to-vote', false],
                    ["Who's Running", '/whos-running', false],
                    ['Official Candidates List', CKL_LIST_URL, true],
                  ].map(([label, to, ext]) => ext ? (
                    <a key={label} href={to} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid={`ward-help-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <ExternalLink className="h-3.5 w-3.5" /> {label}
                    </a>
                  ) : (
                    <Link key={label} to={to} className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid={`ward-help-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </CardContent></Card>
        </section>

        {/* Candidate updates feed */}
        <div className="mt-12">
          <SectionHeader
            kicker="CANDIDATE UPDATES"
            title="Election updates"
            dek="New filings, withdrawals, certifications, debates and official election notices, source-based, with official information kept separate from Steve's commentary."
          />
          {updates.length === 0 ? (
            <EmptyState title="No published candidate updates yet" subtitle="Verified updates appear here as the campaign team reviews and publishes them." />
          ) : (
            <div className="space-y-3" data-testid="candidate-updates-feed">
              {updates.map((u) => <UpdateCard key={u.id} u={u} />)}
            </div>
          )}
        </div>

        {/* Bottom neutrality disclaimer */}
        <p className="mt-10 text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="whos-running-bottom-disclaimer">
          <ShieldCheck className="inline h-3.5 w-3.5 mr-1 text-slate-400" />
          {meta?.public_disclaimer} {meta?.links_disclaimer} This page is a resource provided by the Steve Lesperance campaign. It is not an official City of Kawartha Lakes website.
        </p>
      </div>

      {/* Ward details drawer */}
      <WardDetailsDrawer
        ward={detailsWard}
        wards={wards}
        candidates={candidates}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onViewCandidates={viewCandidatesInWard}
      />
    </div>
  );
}
